/**
 * "HH:MM" (24-hour, Europe/Dublin) for an ISO datetime string — the house
 * clock, see the Dublin formatters below. Falls back to the raw string on a
 * malformed input rather than throwing — callers render this straight into
 * the UI.
 */
export const formatTime = (iso: string | undefined | null): string =>
  iso ? formatDublinTime(iso) || iso : '';

export const calculateAge = (dateOfBirth: string | undefined | null): number | null => {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

/**
 * The calendar date in Europe/Dublin, "YYYY-MM-DD", for the given instant
 * (defaults to now).
 *
 * Deliberately uses Intl's IANA tz database rather than reimplementing the
 * IST/GMT DST math the server does in src/daily-checklists/utils/operating-
 * day.ts — a naive `new Date().toISOString().slice(0, 10)` is wrong after
 * 23:00 during Irish Summer Time, which is exactly when a closing checklist
 * gets filled in (the bug the cleaning module shipped and this module exists
 * to avoid repeating).
 */
export const dublinDateString = (d: Date = new Date()): string => {
  // en-CA formats as YYYY-MM-DD, which is the exact shape the checklist-runs
  // API expects for `from`/`to`/`date`.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
};

/**
 * Whether an ISO date string falls within [today, today + days] inclusive.
 * Plain string-parsed date comparison, same approach as the server's
 * deriveVettingStatus — no Date-object timezone drift. Shared by the staff
 * compliance register's "expiring soon" filter and the dashboard tile so the
 * two never drift apart on what "expiring soon" means.
 */
export const withinDays = (dateStr: string | undefined, today: string, days: number): boolean => {
  if (!dateStr) return false;
  const target = new Date(dateStr);
  const from = new Date(today);
  const diffDays = Math.floor((target.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= days;
};

/**
 * A zone-less civil date (YYYY-MM-DD) rendered for display, e.g. "1 Jul 2025".
 * Parses the Y/M/D parts directly rather than `new Date(str)` so a date-only
 * string is never shifted a day by the browser's timezone. Returns '' for a
 * missing or malformed value so callers can drop it into a template safely.
 */
export const formatCivilDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  // Noon UTC anchor keeps the day stable regardless of the viewer's timezone.
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/** "Wed 26 Aug" — a civil date with its weekday, for copy that names a day
 *  in the recent past ("since Wed 26 Aug"). Same noon-UTC anchor as
 *  formatCivilDate; '' for a missing or malformed value. */
export const formatCivilDateWithWeekday = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  // Assembled from parts: en-IE renders "Mon, 17 Aug" and the comma reads
  // oddly mid-sentence ("since Mon, 17 Aug").
  const parts = new Intl.DateTimeFormat('en-IE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).formatToParts(new Date(Date.UTC(year, month - 1, day, 12)));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')} ${part('day')} ${part('month')}`;
};

/**
 * A zone-less civil date (YYYY-MM-DD) shifted by `days` (negative walks back),
 * returned in the same shape. Same noon-UTC anchor as formatCivilDate: parsing
 * the Y/M/D parts and anchoring at 12:00 UTC means no DST transition and no
 * viewer timezone can push the result onto the wrong calendar day. Returns ''
 * for a missing or malformed input, matching formatCivilDate's contract.
 */
export const addDays = (dateStr: string | undefined | null, days: number): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  const anchored = new Date(Date.UTC(year, month - 1, day + days, 12));
  return anchored.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// House formats for TIMESTAMPS (ISO datetime strings). One locale (en-IE),
// one clock (24-hour), one timezone (Europe/Dublin — the location's wall
// clock, not the viewer's). Every screen showing a stored instant renders
// this way — through these three, or (where a bespoke shape is needed:
// relative-time cutoffs, print headers) with the same locale + timeZone
// options inline — so an incident or medication timestamp can never render
// US-ordered, and a single screen can never mix "19:28" with "07:28 PM".
//
// For DATE-ONLY strings ("YYYY-MM-DD") use formatCivilDate above — it is
// immune to the UTC-midnight day-shift. These are for instants.
// ---------------------------------------------------------------------------

const DUBLIN_DATE = new Intl.DateTimeFormat('en-IE', {
  timeZone: 'Europe/Dublin',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DUBLIN_TIME = new Intl.DateTimeFormat('en-IE', {
  timeZone: 'Europe/Dublin',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DUBLIN_DATE_TIME = new Intl.DateTimeFormat('en-IE', {
  timeZone: 'Europe/Dublin',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** "27 Aug 2026" from an ISO instant, on the Dublin calendar. */
export const formatDublinDate = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DUBLIN_DATE.format(d);
};

/** "19:28" (24-hour) from an ISO instant, on the Dublin clock. */
export const formatDublinTime = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DUBLIN_TIME.format(d);
};

/** "27 Aug 2026, 19:28" from an ISO instant. */
export const formatDublinDateTime = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DUBLIN_DATE_TIME.format(d);
};

/** Hour of the day (0–23) in Europe/Dublin, for time-of-day defaults. */
export const dublinHour = (d: Date = new Date()): number =>
  parseInt(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Dublin', hour: 'numeric', hour12: false })
      .format(d)
      .replace(/\D/g, ''),
    10
  ) % 24;

/** The Europe/Dublin wall-clock time as "HH:mm" (24-hour), comparable with a class's start time. */
export const dublinTimeHHmm = (d: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
